import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import {
  appendWasrapport,
  fetchWasserijItem,
  updateWasserijItem,
  type WasserijItemDetail,
} from '../../lib/wasserij-items';
import { ITEM_STATUS_COLORS } from '../../lib/order-constants';
import { isPerStukPrimary } from '../../lib/order-edit-rules';
import { formatDateTime } from '../../lib/format';
import { formatDimensions } from '../../lib/item-helpers';
import { KleedCard } from './KleedCard';
import { MediaGrid } from '../MediaGrid';
import { KlantlinkKopieren } from '../KlantlinkKopieren';
import { SnijplanSection } from './SnijplanSection';
import { PackageEditor } from './PackageEditor';
import { ExtrasEditor } from './ExtrasEditor';
import { PriceBreakdown } from './PriceBreakdown';
import { WasrapportTimeline } from './WasrapportTimeline';
import { KleedTijdlijn } from '../order-detail/KleedTijdlijn';

interface Props {
  itemId: string;
}

const INPAK_VERBORGEN = ['Ingepakt', 'Klaargelegd', 'In bus', 'Afgeleverd', 'Geannuleerd'];

export function ItemDetailView({ itemId }: Props) {
  const [tijdlijnLeeg, setTijdlijnLeeg] = useState(false);
  const [item, setItem] = useState<WasserijItemDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  // Workflow draft
  const [status, setStatus] = useState('');
  const [voorinspectieGedaan, setVoorinspectieGedaan] = useState(false);
  const [klaarVoorAflevering, setKlaarVoorAflevering] = useState(false);
  const [savingWorkflow, setSavingWorkflow] = useState(false);
  const [workflowError, setWorkflowError] = useState<string | null>(null);

  // Snelactie "Markeer als ingepakt" — corrigeert kleden die de field-app
  // na-inspectie heeft gemist. Stuurt alleen de status; de Apex-service vult
  // Klaar_voor_aflevering + Datum_ingepakt automatisch aan.
  const [markingPacked, setMarkingPacked] = useState(false);
  const [packedError, setPackedError] = useState<string | null>(null);

  // Wasrapport draft
  const [wasrapportDraft, setWasrapportDraft] = useState('');
  const [savingWasrapport, setSavingWasrapport] = useState(false);
  const [wasrapportError, setWasrapportError] = useState<string | null>(null);

  const load = useCallback((mode: 'full' | 'silent' = 'full') => {
    let cancelled = false;
    if (mode === 'full') {
      setLoading(true);
      setError(null);
      setNotFound(false);
    }
    fetchWasserijItem(itemId)
      .then((data) => {
        if (cancelled) return;
        setItem(data);
        setStatus(data.status);
        setVoorinspectieGedaan(data.voorinspectieGedaan);
        setKlaarVoorAflevering(data.klaarVoorAflevering);
        setLoading(false);
      })
      .catch((err: Error & { status?: number }) => {
        if (cancelled) return;
        if (mode === 'full') {
          if (err.status === 404) setNotFound(true);
          else setError(err.message || 'Kon item niet laden');
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [itemId]);

  useEffect(() => load('full'), [load]);

  const reloadSilent = useCallback(() => load('silent'), [load]);

  const [extraPreview, setExtraPreview] = useState<
    Array<{ id: string; name: string; price: number }> | null
  >(null);

  useEffect(() => {
    setExtraPreview(null);
  }, [itemId]);

  const workflowDirty = useMemo(() => {
    if (!item) return false;
    return (
      item.status !== status ||
      item.voorinspectieGedaan !== voorinspectieGedaan ||
      item.klaarVoorAflevering !== klaarVoorAflevering
    );
  }, [item, status, voorinspectieGedaan, klaarVoorAflevering]);

  const notifyUpdated = useCallback(() => {
    if (!item) return;
    window.dispatchEvent(
      new CustomEvent('wasserij-item:updated', { detail: { itemId: item.id } })
    );
  }, [item]);

  const handleSaveWorkflow = async () => {
    if (!item || !workflowDirty) return;
    setSavingWorkflow(true);
    setWorkflowError(null);
    try {
      const patch: Record<string, unknown> = {};
      if (item.status !== status) patch.Status__c = status;
      if (item.voorinspectieGedaan !== voorinspectieGedaan) patch.Voorinspectie_gedaan__c = voorinspectieGedaan;
      if (item.klaarVoorAflevering !== klaarVoorAflevering) patch.Klaar_voor_aflevering__c = klaarVoorAflevering;
      await updateWasserijItem(item.id, patch);
      notifyUpdated();
      reloadSilent();
    } catch (err) {
      setWorkflowError(err instanceof Error ? err.message : 'Opslaan mislukt');
    } finally {
      setSavingWorkflow(false);
    }
  };

  const handleMarkPacked = async () => {
    if (!item || markingPacked) return;
    setMarkingPacked(true);
    setPackedError(null);
    try {
      await updateWasserijItem(item.id, { Status__c: 'Ingepakt' });
      notifyUpdated();
      reloadSilent();
    } catch (err) {
      setPackedError(err instanceof Error ? err.message : 'Markeren mislukt');
    } finally {
      setMarkingPacked(false);
    }
  };

  const handleAppendWasrapport = async () => {
    if (!item) return;
    const entry = wasrapportDraft.trim();
    if (!entry) return;
    setSavingWasrapport(true);
    setWasrapportError(null);
    try {
      await appendWasrapport(item.id, entry, 'Office');
      setWasrapportDraft('');
      notifyUpdated();
      reloadSilent();
    } catch (err) {
      setWasrapportError(err instanceof Error ? err.message : 'Toevoegen mislukt');
    } finally {
      setSavingWasrapport(false);
    }
  };

  if (loading) {
    return (
      <div className="flex-1 p-6 space-y-3 animate-pulse">
        <div className="h-8 w-1/2 bg-slate-100 rounded" />
        <div className="h-32 bg-slate-100 rounded" />
        <div className="h-32 bg-slate-100 rounded" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="flex-1 flex items-center justify-center text-sm text-slate-500">
        Item niet gevonden.
        <Link to="/items" className="ml-2 text-indigo-600 hover:underline">
          Terug naar items
        </Link>
      </div>
    );
  }

  if (error || !item) {
    return (
      <div className="flex-1 p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
          {error || 'Kon item niet laden'}
        </div>
      </div>
    );
  }

  const dimsLabel = formatDimensions(item);
  const toonInpak = !INPAK_VERBORGEN.includes(item.status);

  return (
    <div className="flex-1 overflow-y-auto bg-slate-50">
      <div className="sticky top-0 z-30 isolate bg-white border-b border-slate-200">
        <div className="px-6 py-3">
          <Link
            to="/items"
            className="text-xs text-slate-500 hover:text-slate-700 hover:underline"
          >
            ← Items
          </Link>
          <div className="mt-1 flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2 flex-wrap">
                {item.product.name || 'Item'}
                <span className="text-sm font-mono text-slate-500">{item.itemNumber}</span>
                {item.status && (
                  <span
                    className={clsx(
                      'text-[10px] px-1.5 py-0.5 rounded font-medium',
                      ITEM_STATUS_COLORS[item.status] || 'bg-slate-100 text-slate-600'
                    )}
                  >
                    {item.status}
                  </span>
                )}
              </h1>
              <p className="text-sm text-slate-500 mt-0.5">
                <Link
                  to={`/orders/${item.workOrderId}`}
                  className="text-indigo-600 font-medium hover:underline"
                >
                  {item.workOrderNumber}
                </Link>
                {' · '}
                {dimsLabel}
                {item.areaM2 ? ` · ${item.areaM2.toFixed(2)} m²` : ''}
              </p>
            </div>
            {workflowDirty && (
              <button
                type="button"
                onClick={handleSaveWorkflow}
                disabled={savingWorkflow}
                className="flex-shrink-0 text-sm bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded disabled:opacity-50"
              >
                {savingWorkflow ? 'Opslaan…' : 'Opslaan'}
              </button>
            )}
          </div>
          {workflowError && <p className="mt-2 text-xs text-red-600">{workflowError}</p>}
          {packedError && <p className="mt-2 text-xs text-red-600">{packedError}</p>}
        </div>
      </div>

      <div className="px-6 py-5 space-y-4">
        <Section
          title="Foto's"
          action={
            item.klantlinkUrl ? <KlantlinkKopieren url={item.klantlinkUrl} /> : undefined
          }
        >
          <MediaGrid wiSfId={item.id} />
          {item.photosUrl?.includes('companycam.com') && (
            <a
              href={item.photosUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 inline-block text-sm text-indigo-600 hover:underline"
            >
              CompanyCam openen ↗
            </a>
          )}
        </Section>

        <div className="grid lg:grid-cols-2 gap-4">
          <div className="space-y-4">
            <KleedCard
              item={item}
              onSaved={() => {
                notifyUpdated();
                reloadSilent();
              }}
            />

            <SnijplanSection
              item={item}
              onSaved={() => {
                notifyUpdated();
                reloadSilent();
              }}
            />

            <Section title="Voorinspectie">
              {item.voorinspectie.length > 0 ? (
                <ul className="text-sm text-slate-700 space-y-1 list-disc list-inside">
                  {item.voorinspectie.map((v) => (
                    <li key={v}>{v}</li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-slate-400">Geen bevindingen geregistreerd.</p>
              )}
              {item.voorinspectieOpmerkingen && (
                <div className="mt-2 text-sm text-slate-700 whitespace-pre-wrap">
                  {item.voorinspectieOpmerkingen}
                </div>
              )}
              {item.voorinspectieRandPrijs != null && (
                <p className="mt-2 text-xs text-slate-500">
                  Rand-reparatie ingeschat: € {item.voorinspectieRandPrijs.toFixed(2)}
                </p>
              )}
            </Section>

            {item.naInspectie.length > 0 && (
              <Section title="Na-inspectie">
                <ul className="text-sm text-slate-700 space-y-1 list-disc list-inside">
                  {item.naInspectie.map((v) => (
                    <li key={v}>{v}</li>
                  ))}
                </ul>
              </Section>
            )}
          </div>

          <div className="space-y-4">
            <Section title="Status">
              <div className="flex flex-wrap items-center gap-3">
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="text-sm border border-slate-300 rounded px-2 py-1.5"
                >
                  <option value="">— kies status —</option>
                  {item.picklists.status.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
                {toonInpak && (
                  <button
                    type="button"
                    onClick={handleMarkPacked}
                    disabled={markingPacked}
                    className="text-sm bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded disabled:opacity-50"
                    title="Zet status op Ingepakt + klaar voor aflevering + inpakdatum vandaag"
                  >
                    {markingPacked ? 'Bezig…' : 'Markeer als ingepakt'}
                  </button>
                )}
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-4">
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={voorinspectieGedaan}
                    onChange={(e) => setVoorinspectieGedaan(e.target.checked)}
                    className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  Voorinspectie gedaan
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={klaarVoorAflevering}
                    onChange={(e) => setKlaarVoorAflevering(e.target.checked)}
                    className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  Klaar voor aflevering
                </label>
              </div>
            </Section>

            {item.product.isPrimary && !isPerStukPrimary(item.product.code) && (
              <PackageEditor
                itemId={item.id}
                parentProductId={item.product.id}
                currentPackage={item.package}
                onChanged={reloadSilent}
              />
            )}
            {item.product.isPrimary && (
              <ExtrasEditor
                itemId={item.id}
                parentProductId={item.product.id}
                extras={item.extras}
                packageCode={item.package?.code ?? null}
                onChanged={reloadSilent}
                onPreviewChange={setExtraPreview}
              />
            )}

            <PriceBreakdown
              breakdown={item.priceBreakdown}
              areaM2={item.areaM2}
              productName={item.product.name || 'item'}
              previewExtras={extraPreview}
            />
          </div>
        </div>

        <Section title="Wasrapport">
          <div className="space-y-3">
            <textarea
              value={wasrapportDraft}
              onChange={(e) => setWasrapportDraft(e.target.value)}
              rows={2}
              placeholder="Notitie toevoegen aan wasrapport…"
              className="w-full text-sm border border-slate-300 rounded px-2 py-1.5"
            />
            <div className="flex items-center justify-end gap-2">
              {wasrapportError && (
                <span className="text-xs text-red-600">{wasrapportError}</span>
              )}
              <button
                type="button"
                onClick={handleAppendWasrapport}
                disabled={savingWasrapport || !wasrapportDraft.trim()}
                className="text-sm bg-slate-800 hover:bg-slate-900 text-white px-3 py-1.5 rounded disabled:opacity-50"
              >
                {savingWasrapport ? 'Opslaan…' : 'Toevoegen'}
              </button>
            </div>
            {tijdlijnLeeg ? (
              item.wasrapport ? (
                <WasrapportTimeline raw={item.wasrapport} />
              ) : (
                <p className="text-sm text-slate-400">Nog geen wasrapport.</p>
              )
            ) : (
              <KleedTijdlijn
                itemId={item.id}
                onLoaded={(r) => setTijdlijnLeeg(r.kleden.every((k) => k.tijdlijn.length === 0))}
              />
            )}
          </div>
        </Section>

        <details className="text-sm text-slate-500">
          <summary className="cursor-pointer select-none hover:text-slate-700">Details</summary>
          <div className="mt-2 bg-white border border-slate-200 rounded-lg p-4">
            <InfoRow label="Aangemaakt" value={formatDateTime(item.createdDate)} />
            <InfoRow label="Laatst gewijzigd" value={formatDateTime(item.lastModifiedDate)} />
            {item.mutatieBron && <InfoRow label="Laatste bron" value={item.mutatieBron} />}
          </div>
        </details>
      </div>
    </div>
  );
}

function Section({
  title,
  action,
  children,
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs uppercase tracking-wide text-slate-500 font-medium">{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline gap-3 py-1 text-sm">
      <span className="w-32 text-slate-500 flex-shrink-0">{label}</span>
      <span className="text-slate-900">{value}</span>
    </div>
  );
}
